/* eslint-disable react/display-name */
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@material-ui/core'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { BomItemController } from 'controllers/Project/BomItemController'
import { useController } from 'customHooks/useController'
import { useMaterialTableLocalization } from 'customHooks/useMaterialTableLocalization'
import { RowThumbnailButton } from 'features/BillOfMaterials/components/BomItemThumbnailButton'
import { ConfirmationDialogProps } from 'features/ConfirmationDialog/ConfirmationDialog'
import { useDialogManager } from 'features/DialogManager/DialogManager'
import _ from 'lodash'
import MaterialTable from 'material-table'
import { AssemblySimplifyModel } from 'model/AssemblySimplifyModel'
import { AssemblyHeaderRow, BoMItemType } from 'model/Project/BoMItemRow'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import {
  AssemblyHeaderDto,
  RowDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { RootAppState, useAppDispatch } from 'store/configureStore'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { DialogCloseSaveButtons } from '../DialogActionButtons/DialogCloseSaveButtons'
import { LoadingContainer } from '../LoadingContainer'
import './AssemblySimplificationDialog.scss'

export type AssemblySimplificationDialogProps = {
  assemblyId: string
  open: boolean
  onClose: () => void
}

type HashGroup = {
  [assemblyId: string]: {
    [hash: string]: {
      partTypeIds: string[]
    }
  }
}

const HashGroupTable = (props: {
  rowDtos: RowDto[]
  selectedAssemblyId
  hashGroupKey
  setSelectedRows
  selectedRows
  isClosing
}) => {
  const localization = useMaterialTableLocalization()
  const [rowDtosWithTableData, setRowDtosWithTableData] = useState<
    Array<RowDto & { tableData: { checked: boolean } }>
  >([])

  React.useEffect(() => {
    if (!props.selectedAssemblyId) {
      setRowDtosWithTableData([])
    } else {
      setRowDtosWithTableData(
        props.rowDtos.map((row) => {
          return {
            ...row,
            tableData: {
              checked:
                !_.isEmpty(props.selectedRows) &&
                props.selectedRows[props.selectedAssemblyId] &&
                props.selectedRows[props.selectedAssemblyId][
                  props.hashGroupKey
                ]?.partTypeIds?.includes(row.id),
            },
          }
        })
      )
    }
  }, [
    props.hashGroupKey,
    props.rowDtos,
    props.selectedAssemblyId,
    props.selectedRows,
  ])

  return (
    <>
      <MaterialTable
        options={{
          emptyRowsWhenPaging: false,
          paging: false,
          search: false,
          showSelectAllCheckbox: true,
          selection: true,
          toolbar: false,
          padding: 'dense',
          tableLayout: 'auto',
        }}
        localization={localization}
        columns={[
          {
            title: 'row number',
            field: 'rowNumber',
            width: '5%',
          },
          {
            title: 'thumbnail',
            render: (partType) => (
              <RowThumbnailButton
                rowId={partType.id}
                bomItemType={BoMItemType.row}
                disableClick
                showTooltip
              />

              //   <img
              //     src={projectImageUrl(
              //       organizationId,
              //       projectId,
              //       partType.thumbnail3d
              //     )}
              //   />
            ),
            sorting: false,
            width: '10%',
          },
          {
            title: 'name',
            field: 'name',
            width: '60%',
          },
          {
            title: 'dimensions',
            field: 'dimensionsSummary',
            width: '25%',
            cellStyle: {
              width: '100',
              minWidth: '240px',
              maxWidth: '240px',
            },
            headerStyle: {
              width: '10px',
              minWidth: '10px',
              maxWidth: '10px',
            },
          },
          // {
          //     field: 'id'
          // }
        ]}
        data={rowDtosWithTableData}
        onSelectionChange={(rows) => {
          setTimeout(() => {
            props.setSelectedRows((current) => {
              const newSelectedRows = { ...current }

              newSelectedRows[props.selectedAssemblyId] = {
                ...newSelectedRows[props.selectedAssemblyId],
                [props.hashGroupKey]: {
                  partTypeIds: rows.map((x) => x.id),
                },
              }

              return newSelectedRows
            })
          })
        }}
      />
    </>
  )
}

export const AssemblySimplificationDialog = (
  props: AssemblySimplificationDialogProps
) => {
  const { partyId: organizationId, projectId } =
    useOrganizationAndProjectContext()

  const { controller, loading } = useController(
    () => new BomItemController(organizationId, projectId)
  )

  const { openDialog } = useDialogManager()

  const bomItems = useAppSelector(
    (state: RootAppState) => state.project.flatView,
    _.isEqual
  )
  const dispatch = useAppDispatch()

  const [data, setData] = useState<AssemblySimplifyModel[]>(undefined)
  const [selectedAssembly, setSelectedAssembly] =
    useState<AssemblyHeaderDto>(undefined)

  const [selectedRows, setSelectedRows] = useState<HashGroup>({})

  React.useEffect(() => {
    ;(async () => {
      const data = await controller.GetPartTypeGroups()
      if (data?.length) {
        setData(data)
        // setSelectedAssembly(bomItems[data[0].assemblyId] as AssemblyHeaderRow)
        setSelectedAssembly(bomItems[props.assemblyId] as AssemblyHeaderRow)
      }
    })()

    return () => {
      setData(undefined)
      setSelectedAssembly(undefined)
      setSelectedRows({})
      setIsClosing(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller])

  React.useEffect(() => {
    if (selectedAssembly && !selectedRows[selectedAssembly.assembly.id]) {
      setSelectedRows((curr) => {
        const newSelectedRows = { ...curr }
        const assemblyHashGroups = data.find(
          (x) => x.assemblyId === selectedAssembly.assembly.id
        )?.hashGroups

        assemblyHashGroups &&
          Object.keys(assemblyHashGroups).forEach((hashGroup) => {
            Object.assign(newSelectedRows, {
              ...newSelectedRows,
              [selectedAssembly.assembly.id]: {
                ...newSelectedRows[selectedAssembly.assembly.id],
                [hashGroup]: {
                  partTypeIds: assemblyHashGroups[hashGroup].partTypeIds,
                },
              },
            })
          })

        return newSelectedRows
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedAssembly])

  const [isClosing, setIsClosing] = useState(false)
  const { t } = useTranslation()

  const handleClose = () => {
    props.onClose()
  }

  const handleSave = async () => {
    try {
      openDialog('ConfirmationDialog', {
        title: t('project:assembly-simplification-title'),
        onConfirm: () => {
          Object.keys(selectedRows).forEach(async (assemblyId) => {
            await controller.SetPartTypeGroups(
              assemblyId,
              _.flatten(
                Object.values(selectedRows[assemblyId]).map(
                  (x) => x.partTypeIds
                )
              ),
              dispatch
            )
            handleClose()
          })
        },
        onCancel: () => {
          openDialog('AssemblySimplification', props)
        },
      } as ConfirmationDialogProps)
    } catch (err) {
      ShowException('simplification', err)
    }
  }

  return (
    <Dialog
      open={props.open}
      onClose={() => props.onClose()}
      maxWidth="lg"
      fullWidth
      className="assembly-simplification"
    >
      <DialogTitle>
        <LocalizedTypography
          translationKey={'project:assembly-simplification-title'}
        >
          assembly simplification
        </LocalizedTypography>
      </DialogTitle>

      <DialogContent className="assembly-simplification-content">
        <DialogContentText>
          {selectedAssembly && (
            <div className="assembly-intro">
              {selectedAssembly?.assembly.thumbnail3d ? (
                <RowThumbnailButton
                  rowId={props.assemblyId}
                  bomItemType={BoMItemType.assembly}
                  disableClick
                  showTooltip
                  size="large"
                />
              ) : null}
              <Typography variant="h6">
                {selectedAssembly?.assembly.name}
              </Typography>
            </div>
          )}
        </DialogContentText>
        <LoadingContainer
          noMessage
          loading={data === undefined && loading['get-part-type-groups']}
          empty={
            (data === undefined ||
              data.filter((x) => x.assemblyId === props.assemblyId).length ===
                0) && <Typography>no duplicated parts found</Typography>
          }
        >
          {data?.length > 0 && (
            <>
              <div className="assembly-list"></div>
              {selectedAssembly && (
                <div className="assembly-group">
                  {data
                    .filter(
                      (x) => x.assemblyId === selectedAssembly.assembly.id
                    )
                    .map((assemblyData) => {
                      return Object.keys(assemblyData.hashGroups).map(
                        (hashGroupKey) => {
                          return (
                            <HashGroupTable
                              key={hashGroupKey}
                              rowDtos={
                                Object.values(bomItems).filter(
                                  (x) =>
                                    x.type === 'Row' &&
                                    assemblyData.hashGroups[
                                      hashGroupKey
                                    ].partTypeIds.includes(x.id)
                                ) as RowDto[]
                              }
                              hashGroupKey={hashGroupKey}
                              selectedAssemblyId={selectedAssembly.assembly.id}
                              selectedRows={selectedRows}
                              setSelectedRows={setSelectedRows}
                              isClosing={isClosing}
                            />
                          )
                        }
                      )
                    })}
                </div>
              )}
            </>
          )}
        </LoadingContainer>
      </DialogContent>
      <DialogActions>
        <DialogCloseSaveButtons
          onCloseButtonClicked={handleClose}
          onSaveButtonClicked={handleSave}
          saveButtonDisabled={!Object.keys(selectedRows).length}
        />
      </DialogActions>
    </Dialog>
  )
}
